import { colors } from '@lmc/common/constants'
import type { CarerSkill } from '@lmc/ts-common'
import {
  Button,
  InputDatePicker,
  InputSelect,
  InputText,
  Modal,
  Popover
} from '@lmc/ui-kit'
import { Form } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showToast } from '../../../../common/utils'
import type { CarerSkillOption, SkillOptionType } from './CarerSkills'

type CarerSkillModalProps = {
  isModalVisible: boolean
  handleToggleModal: () => void
  handleToogleDeleteModal: () => void
  addSkill: (skill: CarerSkill, id: string) => Promise<unknown>
  updateSkill: (
    skill: CarerSkill,
    id: string,
    skillId: string
  ) => Promise<unknown>
  skills?: CarerSkill[]
  carerSkillDetail?: CarerSkill
  carerSkillOptions?: CarerSkillOption[]
  skillsFlattenOptions?: SkillOptionType[]
  carerId?: string
  hasCreatePermission?: boolean
  hasUpdatePermission?: boolean
  hasDeletePermission?: boolean
  testId?: string
}

export const CarerSkillsModal = ({
  isModalVisible,
  handleToggleModal,
  handleToogleDeleteModal,
  addSkill,
  updateSkill,
  skills,
  carerSkillDetail,
  carerSkillOptions = [],
  skillsFlattenOptions,
  carerId,
  hasCreatePermission,
  hasUpdatePermission,
  hasDeletePermission,
  testId
}: CarerSkillModalProps): React.ReactElement => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const onConfirm = () => {
    form.submit()
  }

  const [formChange, setFormChange] = useState<CarerSkill | undefined>(
    carerSkillDetail
  )

  const [loading, setLoading] = useState(false)

  const handleCloseForm = () => {
    handleToggleModal()
    handleCleanForm()
  }

  const handleCleanForm = () => {
    form.resetFields()
    form.setFieldsValue({})
    setFormChange(undefined)
    setLoading(false)
  }

  const selectedSkillOptionType = skillsFlattenOptions?.find(
    (option) => option.id === (formChange?.skill || carerSkillDetail?.skill)
  )

  const selectedTemplate = selectedSkillOptionType?.template

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (carerSkillDetail) {
      form.setFieldsValue({
        skill: carerSkillDetail.skill,
        ...(carerSkillDetail?.trainingCompletedDate && {
          trainingCompletedDate: dayjs(carerSkillDetail?.trainingCompletedDate)
        })
      })
      handleToggleModal()
    }
    if (!carerSkillDetail) {
      handleCleanForm()
    }
  }, [carerSkillDetail])

  const handleFinish = () => {
    setLoading(false)
    showToast({ title: t('general.forms.successfullySaved') })
    handleCloseForm()
  }

  const canDeleteSkill = selectedTemplate?.mandatory

  const onFinish = async (values: CarerSkill) => {
    setLoading(true)

    const payload = {
      skill: values?.skill,
      ...(values?.trainingCompletedDate && {
        trainingCompletedDate: dayjs(values?.trainingCompletedDate).format(
          'DD-MM-YYYY'
        )
      })
    }

    try {
      let res

      if (carerSkillDetail) {
        res = await updateSkill?.(
          payload as CarerSkill,
          carerId as string,
          carerSkillDetail.id
        )
      } else {
        res = await addSkill?.(payload as CarerSkill, carerId as string)
      }

      if (res) {
        handleFinish()
      }
      setLoading(false)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const renderDeleteButton = carerSkillDetail && (
    <Button
      variant="danger"
      onClick={handleToogleDeleteModal}
      disabled={canDeleteSkill}
      disabledByPermission={!hasDeletePermission}
    >
      {t('skillsAndNeeds.deleteSkill')}
    </Button>
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (selectedTemplate && !selectedTemplate?.trainingRequired) {
      form.setFieldsValue({ trainingCompletedDate: null })
    }
  }, [selectedTemplate])

  return (
    <Modal
      localisation={{
        title: t(
          `skillsAndNeeds.${
            carerSkillDetail ? 'editAssignSkill' : 'assignSkill'
          }`
        ),
        confirmText: t(`general.buttons.confirm`),
        cancelText: t(`general.buttons.cancel`)
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{
        isLoading: loading,
        variant: 'primary',
        disabledByPermission: carerSkillDetail
          ? !hasUpdatePermission
          : !hasCreatePermission
      }}
      testId={testId}
      extraFooterElement={
        canDeleteSkill ? (
          <Popover
            content={
              <div style={styles.toolTip}>
                <span style={styles.toolTipDescription}>
                  {t('skillsAndNeeds.cannotDeleteSkillBold')}
                </span>
                {t('skillsAndNeeds.cannotDeleteSkill')}
              </div>
            }
          >
            <div>{renderDeleteButton}</div>
          </Popover>
        ) : (
          renderDeleteButton
        )
      }
    >
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={(values: CarerSkill) =>
          setFormChange({ ...formChange, ...values })
        }
        data-testid={testId ? `${testId}-form` : undefined}
      >
        <InputSelect
          testId={testId ? `${testId}-skill-select` : 'skill-select'}
          showSearch
          errorMessage={t('skillsAndNeeds.form.skillNameRequired')}
          type="skill"
          required
          label={t('skillsAndNeeds.form.skillName')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.selectSkill')}
          groupedOptions={carerSkillOptions?.map((skillOption) => ({
            label: skillOption.name,
            options: skillOption?.skills?.map((skill) => ({
              label: skill.name,
              value: skill.id,
              disabled: skills?.map((d) => d.skill).includes(skill.id) || false
            }))
          }))}
          disabled={!!carerSkillDetail}
          data-testid={testId ? `${testId}-skill-select` : undefined}
        />
        <InputText
          label={t('skillsAndNeeds.form.trainingName')}
          layout="column"
          className="ant-clean-input"
          disabled
          style={{ width: '100%' }}
          value={
            (form.getFieldValue('skill') && selectedTemplate?.trainingName) ||
            '-'
          }
          data-testid={testId ? `${testId}-training-name` : undefined}
        />
        <InputDatePicker
          label={t('skillsAndNeeds.form.trainingCompleted')}
          required={
            selectedTemplate ? selectedTemplate?.trainingRequired : false
          }
          type="trainingCompletedDate"
          disabled={
            selectedTemplate ? !selectedTemplate?.trainingRequired : false
          }
          pastDate
          data-testid={testId ? `${testId}-training-date` : undefined}
        />
      </Form>
    </Modal>
  )
}

const styles = {
  toolTip: {
    maxWidth: 323
  },
  toolTipDescription: {
    fontWeight: 800
  },
  divider: {
    borderBottom: '1px solid',
    borderColor: colors.grey,
    marginBottom: 5,
    paddingBottom: 10
  }
}

export default CarerSkillsModal
